import { useEffect, useState } from "react";
import {
  ArrowRight,
  Lock,
  ShoppingBag,
  Star,
  GraduationCap,
  Building2,
  Sparkles,
  Megaphone,
  ChefHat,
  Check,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND, HERO_BADGES, HERO_STATS } from "../data/content";
import { Reveal } from "./Reveal";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const STAT_ICONS = {
  GraduationCap,
  Building2,
  Sparkles,
  Megaphone,
  ShoppingBag,
  ChefHat,
};

export const Hero = () => {
  const { user } = useAuth();
  const [activeStat, setActiveStat] = useState(null);
  const [hintedIdx, setHintedIdx] = useState(0);
  const [hintActive, setHintActive] = useState(true);
  const isAgency = user?.role === "agency" || user?.role === "admin";
  const isPrivate = user?.role === "private";

  // Rotate the "tap for info" hint across a random chip every ~2.5s.
  // Stop permanently once the user has opened any chip modal — they've
  // discovered the interaction.
  useEffect(() => {
    if (!hintActive) return;
    const total = HERO_STATS.length;
    const id = setInterval(() => {
      setHintedIdx((prev) => {
        // pick any other index so the hint visibly moves
        let next = Math.floor(Math.random() * total);
        if (next === prev) next = (prev + 1) % total;
        return next;
      });
    }, 2500);
    return () => clearInterval(id);
  }, [hintActive]);

  const openStat = (s) => {
    setHintActive(false); // permanently stop the cycling hint
    setActiveStat(s);
  };
  const firstName = user?.name?.split(" ")[0] || user?.email?.split("@")[0];

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-[76px] sm:pt-[92px] lg:pt-[112px] pb-10 sm:pb-14 lg:pb-20 bg-brand-cream overflow-hidden"
    >
      {/* Decorative grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.25] pointer-events-none z-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(17,17,17,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(17,17,17,0.06) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
        }}
      />

      {/* Branded fleet van — decorative background layer (lg+).
          Slides UNDER the right edge of the team photo so the two read as
          one composition rather than two separate islands. */}
      <div
        aria-hidden="true"
        data-testid="hero-van-layer"
        className="hidden lg:block absolute top-[4%] right-[-4%] w-[72%] h-[68%] pointer-events-none z-[2] opacity-90 mix-blend-multiply"
        style={{
          backgroundImage: "url('/van-branded.png')",
          backgroundSize: "contain",
          backgroundPosition: "right center",
          backgroundRepeat: "no-repeat",
          maskImage:
            "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.05) 18%, rgba(0,0,0,0.35) 38%, rgba(0,0,0,0.75) 60%, rgba(0,0,0,1) 80%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.05) 18%, rgba(0,0,0,0.35) 38%, rgba(0,0,0,0.75) 60%, rgba(0,0,0,1) 80%)",
        }}
      />
      {/* Soft cream "bridge" layer (lg+) — sits between the team photo and
          the van. A wide radial glow that visually unifies the two and
          softens the seam where the photo's right edge meets the van. */}
      <div
        aria-hidden="true"
        data-testid="hero-bridge-layer"
        className="hidden lg:block absolute top-[8%] right-[-2%] w-[55%] h-[60%] pointer-events-none z-[1]"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(255,248,232,0.85) 0%, rgba(255,248,232,0.45) 35%, rgba(255,248,232,0) 70%)",
        }}
      />
      {/* Mobile / tablet variant — sits in the gap to the right of the headline.
          Rear of the van faces the headline text; left edge fades to transparent
          so the rear bleeds into the cream + text seamlessly. */}
      <div
        aria-hidden="true"
        data-testid="hero-van-layer-mobile"
        className="lg:hidden absolute top-[120px] sm:top-[150px] right-[-32%] sm:right-[-22%] w-[95%] sm:w-[80%] h-[260px] sm:h-[340px] pointer-events-none z-[1] opacity-55 sm:opacity-65"
        style={{
          backgroundImage: "url('/van-branded.png')",
          backgroundSize: "contain",
          backgroundPosition: "right center",
          backgroundRepeat: "no-repeat",
          maskImage:
            "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.2) 28%, rgba(0,0,0,0.75) 60%, rgba(0,0,0,1) 95%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.2) 28%, rgba(0,0,0,0.75) 60%, rgba(0,0,0,1) 95%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 z-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Text */}
          <div className="lg:col-span-6">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-black/5 px-3.5 py-1.5 shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-green animate-pulse" />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/80">
                  Now Booking · NYC + Long Island
                </span>
              </div>
            </Reveal>

            <Reveal delay={1}>
              <h1 className="mt-5 sm:mt-6 font-display font-black text-[36px] sm:text-[54px] lg:text-[64px] leading-[1.05] sm:leading-[1.02] tracking-[-0.03em] text-brand-charcoal">
                <span className="relative inline-block whitespace-nowrap">
                  <span className="relative z-10">Catering</span>
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-1.5 sm:right-2 bottom-1 h-3 lg:h-4 bg-brand-teal/35 rounded-sm"
                  />
                </span>{" "}
                Built for
                <span className="block">Schools, Agencies,</span>
                <span className="block">
                  <span className="relative inline-block whitespace-nowrap">
                    <span className="relative z-10">Offices</span>
                    <span
                      aria-hidden="true"
                      className="absolute left-0 right-1.5 sm:right-2 bottom-1 h-3 lg:h-4 bg-brand-teal/35 rounded-sm"
                    />
                  </span>{" "}
                  &amp;{" "}
                  <span className="relative inline-block whitespace-nowrap">
                    <span className="relative z-10">Events.</span>
                    <span
                      aria-hidden="true"
                      className="absolute left-0 right-1.5 sm:right-2 bottom-1 h-3 lg:h-4 bg-brand-gold/70 rounded-sm"
                    />
                  </span>
                </span>
              </h1>
            </Reveal>

            <Reveal delay={2}>
              <p className="mt-6 max-w-xl text-[16px] lg:text-[18px] leading-relaxed text-brand-charcoal/75">
                <strong className="text-brand-charcoal">{BRAND.name}</strong> — powered by
                Burgers Tacos Wings &amp; Seafood (BTWNYC) — delivers reliable,
                crowd-pleasing catering backed by quick-service experience, community
                roots, and professional food-service execution.
              </p>
            </Reveal>

            <Reveal delay={3}>
              {isAgency ? (
                <div
                  data-testid="hero-cta-agency"
                  className="mt-7 sm:mt-8 max-w-md"
                >
                  <Link
                    to="/agency"
                    data-testid="hero-agency-continue-btn"
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-4 text-[12.5px] sm:text-[13px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-all hover:scale-[1.02] shadow-[0_18px_40px_-18px_rgba(17,17,17,0.6)]"
                  >
                    <Lock className="h-4 w-4" />
                    Continue to Agency Portal
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                  <p className="mt-3 text-[12px] text-brand-charcoal/65 leading-snug">
                    Welcome back{firstName ? `, ${firstName}` : ""}. Institutional
                    pricing &amp; PO checkout are enabled on your account.
                  </p>
                </div>
              ) : isPrivate ? (
                <div
                  data-testid="hero-cta-private"
                  className="mt-7 sm:mt-8 max-w-md"
                >
                  <Link
                    to="/menu"
                    data-testid="hero-private-continue-btn"
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-4 text-[12.5px] sm:text-[13px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-all hover:scale-[1.02] shadow-[0_18px_40px_-18px_rgba(17,17,17,0.6)]"
                  >
                    <ShoppingBag className="h-4 w-4" />
                    Continue to the Menu
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                  <p className="mt-3 text-[12px] text-brand-charcoal/65 leading-snug">
                    Welcome back{firstName ? `, ${firstName}` : ""}. Card checkout
                    enabled. Delivery requires a $400 minimum (waived for pickup).
                  </p>
                </div>
              ) : (
                <>
                  <div className="mt-7 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                    <Link
                      to="/agency"
                      data-testid="hero-agency-portal-btn"
                      className="group inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-4 text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[0.08em] hover:bg-brand-teal transition-all hover:scale-[1.02] shadow-[0_18px_40px_-18px_rgba(17,17,17,0.6)] whitespace-nowrap"
                    >
                      <Lock className="h-3.5 w-3.5" />
                      Agency Portal
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </Link>
                    <Link
                      to="/register"
                      state={{ from: "/menu", gate: "menu" }}
                      data-testid="hero-private-catering-btn"
                      className="group inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-teal text-white px-4 py-4 text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[0.08em] hover:bg-brand-teal-dark transition-all hover:scale-[1.02] shadow-[0_18px_40px_-18px_rgba(0,166,178,0.55)] whitespace-nowrap"
                    >
                      <ShoppingBag className="h-3.5 w-3.5" />
                      Private Catering / See Our Menu
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                  <p className="mt-3 text-[11px] font-medium text-brand-charcoal/55 max-w-xl leading-snug">
                    <span className="font-bold uppercase tracking-wider text-brand-teal">Agency:</span>{" "}
                    login + Purchase Order ·{" "}
                    <span className="font-bold uppercase tracking-wider text-brand-gold-dark">
                      Private:
                    </span>{" "}
                    free account to see the menu &amp; order
                  </p>
                </>
              )}
            </Reveal>

            {/* Badges */}
            <Reveal delay={4}>
              <ul className="mt-10 flex flex-wrap gap-2.5" data-testid="hero-badges">
                {HERO_BADGES.map((b) => (
                  <li
                    key={b}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/5 px-3.5 py-1.5 text-[11.5px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/85 shadow-sm"
                  >
                    <Star className="h-3 w-3 text-brand-gold fill-brand-gold" />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          {/* Image */}
          <div className="lg:col-span-6 relative z-[3]">
            <Reveal delay={2}>
              <div
                className="relative rounded-[20px] sm:rounded-[28px] overflow-hidden"
                style={{
                  maskImage:
                    "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.55) 8%, rgba(0,0,0,0.95) 22%, rgba(0,0,0,1) 45%, rgba(0,0,0,0.85) 65%, rgba(0,0,0,0.45) 82%, rgba(0,0,0,0.15) 92%, transparent 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.55) 8%, rgba(0,0,0,0.95) 22%, rgba(0,0,0,1) 45%, rgba(0,0,0,0.85) 65%, rgba(0,0,0,0.45) 82%, rgba(0,0,0,0.15) 92%, transparent 100%)",
                }}
              >
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/9c8ef01f-de5d-404e-b131-b692bd44c30d/images/2a1bd9b43fba26bd14105ab6e75e7d4796a0f03614a8b4871e5e833c19e94458.png"
                  alt="Fresh Fast Foods catering crew in branded F3 / BTWNYC gear at a corporate outdoor setup"
                  className="w-full h-[360px] sm:h-[520px] lg:h-[600px] object-cover opacity-85"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-brand-cream/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-cream/45 via-transparent to-transparent" />

                {/* Floating chip top-right */}
                <div className="absolute top-4 right-4 sm:top-5 sm:right-5 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-3 sm:px-3.5 py-1.5 sm:py-2 shadow-lg">
                  <span className="h-2 w-2 rounded-full bg-brand-green" />
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-brand-charcoal">
                    Quality Food · Quality People
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Full-width stats row — overlaps the bottom of the image via negative
            margin so it keeps the floating-card look on every viewport. */}
        <Reveal delay={3}>
          <div
            className="relative mt-6 sm:mt-8 lg:mt-14 mx-auto w-full max-w-5xl bg-white border border-black/5 rounded-2xl p-2.5 sm:p-3 shadow-[0_24px_60px_-20px_rgba(17,17,17,0.18)]"
            data-testid="hero-stats"
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-2.5">
              {HERO_STATS.map((s, i) => {
                const isHinted = hintActive && hintedIdx === i;
                return (
                  <button
                    key={s.label}
                    type="button"
                    data-testid={`hero-stat-chip-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => openStat(s)}
                    aria-label={`Learn more about ${s.label}`}
                    className={`group relative rounded-xl bg-brand-cream border px-2 py-2.5 sm:py-3.5 text-center flex items-center justify-center min-h-[44px] sm:min-h-[52px] shadow-[0_2px_8px_-4px_rgba(17,17,17,0.12)] hover:bg-white hover:border-brand-teal/60 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-8px_rgba(17,17,17,0.2)] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal ${
                      isHinted
                        ? "border-brand-teal/70 bg-white hero-stat-glow"
                        : "border-brand-charcoal/10"
                    }`}
                  >
                    <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.06em] sm:tracking-[0.1em] text-brand-charcoal leading-tight whitespace-normal group-hover:text-brand-teal transition-colors">
                      {s.label}
                    </p>
                    {isHinted && (
                      <span
                        data-testid="hero-stat-hint"
                        aria-hidden="true"
                        className="hint-badge absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-brand-charcoal text-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] shadow-lg whitespace-nowrap pointer-events-none"
                      >
                        Tap for info
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>

      {/* Hero stat detail dialog */}
      <Dialog
        open={!!activeStat}
        onOpenChange={(v) => !v && setActiveStat(null)}
      >
        <DialogContent
          data-testid="hero-stat-dialog"
          className="sm:max-w-[460px] p-0 rounded-2xl border-none overflow-hidden"
        >
          {activeStat && (() => {
            const StatIcon = STAT_ICONS[activeStat.icon] || Sparkles;
            return (
              <>
                <div className="bg-brand-charcoal text-white px-6 pt-6 pb-5">
                  <DialogHeader className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-teal/20 text-brand-teal">
                        <StatIcon className="h-5 w-5" />
                      </span>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold">
                        Service Type
                      </p>
                    </div>
                    <DialogTitle className="font-display font-black text-[22px] sm:text-[24px] tracking-tight text-white text-left leading-tight">
                      {activeStat.label}
                    </DialogTitle>
                    <DialogDescription className="text-[13.5px] text-white/75 text-left leading-relaxed">
                      {activeStat.description}
                    </DialogDescription>
                  </DialogHeader>
                </div>
                {activeStat.bullets?.length > 0 && (
                  <ul className="px-6 py-5 space-y-2.5 bg-brand-cream/40">
                    {activeStat.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2.5 text-[13px] text-brand-charcoal leading-snug"
                      >
                        <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-teal text-white">
                          <Check className="h-2.5 w-2.5" />
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-col sm:flex-row gap-2.5 px-6 py-5 border-t border-black/5 bg-white">
                  <Link
                    to="/quote"
                    data-testid="hero-stat-cta-quote"
                    onClick={() => setActiveStat(null)}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal-dark transition-colors"
                  >
                    Request a Quote
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setActiveStat(null)}
                    className="sm:flex-none inline-flex items-center justify-center gap-2 rounded-full bg-white border border-black/10 text-brand-charcoal/70 px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-cream"
                  >
                    <X className="h-3.5 w-3.5" />
                    Close
                  </button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Hero;
